import {useContext, useEffect, useState} from "react"
import {useSearchParams} from "react-router-dom"
import useSWR from "swr"
import {default as cx} from "classnames"
import {Calendar} from "primereact/calendar"

import SavedSurveys from "../../components/surveys/SavedSurveys.jsx"
import SubHeader from "../../components/SubHeader.jsx"
import DeleteSurveyModal from "../../components/surveys/DeleteSurveyModal.jsx"
import {wrappedPost, wrappedDelete} from "../../utilities/AxiosHelper.jsx"
import {fetcherGet, normalizeDate, formatDateForApi, ORIENTATION_STATUS} from "../../utilities/general.jsx"
import RefContext from "../../utilities/RefContext.jsx"
import {useStableNavigate} from "../../utilities/StableNavigateContext"

function NewHireStatusReportSelection() {
    useEffect(() => {
        document.title = "New Hire Status Report Selection"
    }, [])
    const [searchParams] = useSearchParams()
    const initialTab = searchParams.get("tab")
    const [activeTab, setActiveTab] = useState(initialTab || "new")
    const [draftToDelete, setDraftToDelete] = useState(null)
    const [shiftDate, setShiftDate] = useState(normalizeDate(new Date()))

    const {dispatch} = useContext(RefContext)
    const navigate = useStableNavigate()

    const {data: orientations, isLoading: oLoading} = useSWR("/list-orientations?self=true", fetcherGet, {
        fallbackData: [],
    })
    const {
        data: drafts,
        mutate: mutateDrafts,
        loading: rLoading,
    } = useSWR("/surveys/get-incomplete-responses/nhsr", fetcherGet, {
        fallbackData: [],
    })

    const loading = oLoading || rLoading

    const orientationId = orientations.length > 0 ? orientations[0].id : null

    const isPaused = drafts.length > 0 && drafts[0].orientation_status_id === ORIENTATION_STATUS.PAUSED
    const isStartDisabled = isPaused || !orientationId

    const handleDeleteConfirm = () => {
        wrappedDelete(`/surveys/start-response/nhsr/${draftToDelete.id}`)
            .then(() => {
                mutateDrafts()
                setDraftToDelete(null)
            })
            .catch(() => {
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: "Could not delete draft. Please try again.",
                })
            })
    }

    const handleResume = (draft) => {
        navigate(`/surveys/new-hire-status-report/${draft.id}`)
    }

    const startReport = () => {
        if (!orientationId) return

        if (!shiftDate) {
            dispatch("showToast", {
                show: true,
                variant: "danger",
                text: "Shift Date is required",
                timeout: 5000,
            })
            return
        }

        wrappedPost(`/surveys/start-response/nhsr/${orientationId}`, {
            shift_date: formatDateForApi(shiftDate),
        })
            .then((res) => {
                navigate(`/surveys/new-hire-status-report/${res.data.response_id}`)
            })
            .catch(() => {
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: "An error occurred starting the report. Please try again.",
                })
            })
    }

    return (
        <>
            <SubHeader title1="New Hire" title2="Status Report" icon="fa-user-nurse" />
            <div className="container mw-800px px-md-0">
                        <div className="btn-group w-100 my-5" role="tablist">
                <button
                    className={cx("btn btn-light-primary btn-active-primary fw-bold border border-primary", {
                        active: activeTab === "new",
                    })}
                    disabled={isStartDisabled}
                    onClick={() => setActiveTab("new")}
                >
                    Start New NHSR
                </button>
                <button
                    className={cx("btn btn-light-primary btn-active-primary fw-bold border border-primary", {
                        active: activeTab === "saved",
                    })}
                    disabled={isPaused}
                    onClick={() => setActiveTab("saved")}
                >
                    Saved NHSRs
                    {drafts.length > 0 && (
                        <span className="badge badge-square badge-danger shadow-sm ms-2 px-3">{drafts.length}</span>
                    )}
                </button>
            </div>

                {activeTab === "new" && (
                    <div className="card card-flush border pt-3">
                        <div className="card-header">
                            <h3 className="card-title fw-bold fs-2">New Hire Status Report</h3>
                        </div>
                        <div className="card-body pt-0">
                            {!orientationId && <div className="text-dark fs-5">No orientation found.</div>}
                            {orientationId && (
                                <>
                                    <div className="text-dark fs-5 mb-6">
                                        This report will be sent to your unit's orientation team. Please complete it
                                        honestly to help us support your orientation.
                                    </div>
                                    <div>
                                        <label className="text-dark fw-bold fs-3 mt-5 required">NHSR Shift Date</label>
                                        <div className="text-dark fs-5 mb-4">
                                            If back-documenting an NHSR, please indicate the date of the last precepted
                                            shift that this NHSR is reflective of.
                                        </div>
                                        <div className="d-flex gap-4">
                                            <Calendar
                                                showIcon
                                                className="w-100 p-dropdown-tall"
                                                placeholder="Shift Date"
                                                value={shiftDate}
                                                maxDate={new Date()}
                                                onChange={(e) => setShiftDate(normalizeDate(e.value))}
                                            />
                                        </div>
                                    </div>
                                    <div className="py-6">
                                        <button className="btn btn-primary" disabled={loading || isStartDisabled} onClick={startReport}>
                                            {loading ? "Loading..." : "Start NHSR"}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === "saved" && (
                    <div className="card card-flush border pt-3">
                        <div className="card-header">
                            <h3 className="card-title fw-bold fs-2">Saved NHSRs</h3>
                        </div>
                        <div className="card-body pt-0">
                            <SavedSurveys
                                drafts={drafts}
                                onResume={handleResume}
                                onDelete={(draft) => setDraftToDelete(draft)}
                            />
                        </div>
                    </div>
                )}
            </div>
            <DeleteSurveyModal
                draft={draftToDelete}
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDraftToDelete(null)}
                deleteMessage="You have chosen to delete this NHSR. Please confirm your choice or cancel this action
          to return to page."
            />
        </>
    )
}

export default NewHireStatusReportSelection
